import { useIntl } from "react-intl";

import Card from "components/v2/Card/Card";
import Divider from "components/v2/Divider/Divider";
import usePermissions from "hooks/usePermissions";
import { ReactComponent as FeaturedIcon } from "assets/images/iconsV2/featured-icon-gray.svg";
import EmptyState from "components/v2/EmptyState/EmptyState";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import { getFirstAvailable } from "helpers/commonHelpers";
import ShippingPartyDetails from "./ShippingPartyDetails";

interface InvolvedPartiesProps {
  shipmentDetails?: {
    organization: {
      shipper_company?: string;
      provider_company?: string;
      contact_details: {
        addressee: string;
        phone: string;
        email: string;
      };
    };
    schedule: {
      pickup_contact_name?: string;
      pickup_contact_email?: string;
      pickup_contact_phone?: string;
      stop1_contact_name?: string;
      stop1_contact_phone?: string;
      stop1_contact_email?: string;
      consignee: any;
      notify_party: any;
    };
  };
  /**
   * Whether the shipment is off-platform.
   */
  isOffPlatform: boolean;
  /**
   * Whether the shipment data is loading.
   */
  isLoading: boolean;
}

const renderContactDetails = ({ name, email, phone }: { name?: string; email?: string; phone?: string }) => (
  <div className="text-right">
    <p className="text-sm font-medium">{name}</p>
    <a href={`mailto:${email}`}>
      <p className="text-sm text-decoration-underline">{email}</p>
    </a>
    <p className="text-sm">{phone}</p>
  </div>
);

export default function InvolvedParties(props: InvolvedPartiesProps) {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const isProvider = JSON.parse(permissions ?? "{}").user_type === "provider";
  const { isLoading, isOffPlatform, shipmentDetails } = props;

  const isShipmentDetailsEmpty = !shipmentDetails || Object.keys(shipmentDetails).length === 0;

  const { organization, schedule } = shipmentDetails ?? {};
  const { shipper_company, provider_company, contact_details } = organization ?? {};
  const {
    pickup_contact_name,
    pickup_contact_email,
    pickup_contact_phone,

    stop1_contact_name,
    stop1_contact_phone,
    stop1_contact_email,
    consignee,
    notify_party,
  } = schedule ?? {};

  if (isLoading)
    return (
      <div className="mb-3xl">
        <Card borderRadius="md">
          <Card.Header
            title={intl.formatMessage({
              id: "tracking.involvedParties",
              defaultMessage: "Involved parties",
            })}
            showBorder
          />
          <LoadingSkeleton rows={1} />
        </Card>
      </div>
    );
  return (
    <div>
      <Card borderRadius="md">
        <Card.Header
          title={intl.formatMessage({
            id: "tracking.involvedParties",
            defaultMessage: "Involved parties",
          })}
          showBorder
        />
        {!isLoading && isShipmentDetailsEmpty && (
          <Card.Body>
            <EmptyState
              noActions
              customIcon={<FeaturedIcon />}
              title={intl.formatMessage({
                id: "general.couldNotLoadDetails",
                defaultMessage: "We couldn't load these details",
              })}
              description={intl.formatMessage({
                id: "general.tryAgainOrCheckBackLater",
                defaultMessage: "You can try again or check back later.",
              })}
            />
          </Card.Body>
        )}
        {!isLoading && !isShipmentDetailsEmpty && (
          <>
            <Card.LineItem
              label={intl.formatMessage({
                id: isProvider ? "tracking.requestedBy" : "general.provider",
                defaultMessage: isProvider ? "Requested by" : "Provider",
              })}
              rightContent={<p className="text-sm font-medium">{isProvider ? shipper_company : provider_company}</p>}
            />
            {!isOffPlatform && (
              <>
                <div className="pl-3xl">
                  <Divider />
                </div>
                <Card.LineItem
                  label={intl.formatMessage({
                    id: isProvider ? "tracking.requestorContact" : "tracking.providerContact",
                    defaultMessage: isProvider ? "Requestor contact" : "Provider contact",
                  })}
                  rightContent={renderContactDetails({
                    name: contact_details?.addressee,
                    email: contact_details?.email,
                    phone: contact_details?.phone,
                  })}
                />
                {getFirstAvailable(consignee?.address, consignee?.airport, consignee?.seaport) && (
                  <>
                    <div className="pl-3xl">
                      <Divider />
                    </div>
                    <Card.LineItem
                      label={intl.formatMessage({
                        id: "tracking.requestorContacttt",
                        defaultMessage: "Consignee",
                      })}
                      rightContent={
                        <>
                          <ShippingPartyDetails data={consignee} />
                          {consignee.contact_email && consignee.contact_name && consignee.contact_phone && (
                            <div className="mt-md">
                              {renderContactDetails({
                                name: consignee.contact_name,
                                email: consignee.contact_email,
                                phone: consignee.contact_phone,
                              })}
                            </div>
                          )}
                        </>
                      }
                    />
                  </>
                )}
                {getFirstAvailable(notify_party?.address, notify_party?.airport, notify_party?.seaport) && (
                  <>
                    <div className="pl-3xl">
                      <Divider />
                    </div>
                    <Card.LineItem
                      label={intl.formatMessage({
                        id: "tracking.providerContacttt",
                        defaultMessage: "Notify Party",
                      })}
                      rightContent={
                        <>
                          <ShippingPartyDetails data={notify_party} />
                          {notify_party.contact_email && notify_party.contact_name && notify_party.contact_phone && (
                            <div className="mt-md">
                              {renderContactDetails({
                                name: notify_party.contact_name,
                                email: notify_party.contact_email,
                                phone: notify_party.contact_phone,
                              })}
                            </div>
                          )}
                        </>
                      }
                    />
                  </>
                )}
                <div className="pl-3xl">
                  <Divider />
                </div>
                <Card.LineItem
                  label={intl.formatMessage({
                    id: "general.pickupContact",
                    defaultMessage: "Pickup contact",
                  })}
                  rightContent={renderContactDetails({
                    name: pickup_contact_name,
                    email: pickup_contact_email,
                    phone: pickup_contact_phone,
                  })}
                />
                <div className="pl-3xl">
                  <Divider />
                </div>
                <Card.LineItem
                  label={intl.formatMessage({
                    id: "general.deliveryContact",
                    defaultMessage: "Delivery contact",
                  })}
                  rightContent={renderContactDetails({
                    name: stop1_contact_name,
                    email: stop1_contact_email,
                    phone: stop1_contact_phone,
                  })}
                />
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

InvolvedParties.defaultProps = {
  shipmentDetails: {},
};
